/**
 * Game of the year (INVENTORY §1 screen 6, §4.4; prototype lines 314-407).
 *
 * The private board: `YOUR PERSONAL RANKING — NOBODY ELSE SEES THIS`. Nothing
 * here is shared, which is why every read goes through `me()`-scoped maps in
 * `gotyVM` rather than the crew list.
 *
 * TO DO is a one-at-a-time queue, not a list: `gotyQueue` orders the unplaced
 * potentials by insertion index and pushes skipped ids to the BACK in skip
 * order, so `SKIP FOR NOW ↻` rotates rather than removes. `gFeat` is simply
 * `queue[0]`, and placing/skipping advances it as a consequence of the store
 * write — this screen never tracks a cursor.
 *
 * Second long-press drag surface: rows carry `data-gt-zone="goty"` +
 * `data-gt-row="{tier}"` for each `TIERS` entry (`S|A|B|C|D|E|F|X`), capsules
 * `data-gt-cap="{id}"` (see Interests.tsx and lib/drag.ts for the contract).
 *
 * The TO-DO panel is the ninth target — `data-gt-row={GOTY_TODO_ROW}`, which is
 * deliberately not a tier, because dropping there unplaces (`unplaceTier`)
 * instead of placing. Both mutually-exclusive panels carry it: TO DO while a
 * queue remains, ALL PLACED once it is empty, which is precisely when the last
 * placed game still needs somewhere to go back to. Desktop's rail has always
 * had this; the store routes both through `dragEnd`.
 */

import type { PointerEventHandler } from 'react';

import { CoverArt } from '../components/CoverArt.tsx';
import { PageBar } from '../components/PageBar.tsx';
import { TodoThumb } from '../components/TodoThumb.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useDrag } from '../lib/drag.ts';
import type { GameId } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { removeChip } from '../store/selectors.3c.ts';
import { gotyVM } from '../store/selectors.ts';
import type { CapsuleVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

type TierCapsule = CapsuleVM & { shiftX: string; dragOp: string };

function TierTile({
  g,
  onTap,
  onPointerDown,
}: {
  g: TierCapsule;
  onTap: () => void;
  onPointerDown: PointerEventHandler<HTMLDivElement>;
}): JSX.Element {
  const art = useArt(g.id + 'p', g.coverCands);
  return (
    <div
      onClick={onTap}
      onPointerDown={onPointerDown}
      data-gt-cap={g.key}
      style={{
        position: 'relative',
        width: 96,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--gt-border-2)',
        background: 'var(--gt-tile)',
        cursor: 'pointer',
        transform: 'translateX(' + g.shiftX + ')',
        opacity: g.dragOp,
        transition: 'transform .15s ease',
        userSelect: 'none',
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '2/3', background: g.fall, overflow: 'hidden' }}>
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={11} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
        <div style={{ position: 'absolute', inset: 0, background: g.sheen, mixBlendMode: 'screen', pointerEvents: 'none' }} />
      </div>
      <div
        style={{
          padding: '5px 6px',
          fontFamily: MONO,
          fontSize: 8,
          color: 'var(--gt-text-3)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {g.nameUp}
      </div>
    </div>
  );
}

export default function Goty(): JSX.Element {
  const s = useQd();
  const v = gotyVM(s);
  // `gFeat` is a real object even with an empty queue (`coverCands: []`), so the
  // hook is unconditional while the card it paints is not.
  const featArt = useArt(v.gFeat.id + 'p', v.gFeat.coverCands);

  /**
   * This board fetches its own art, the way both Discover screens do.
   *
   * It did not, and the gap was invisible for as long as every game on it was
   * also on the shared list or near the head of the catalog — those are the two
   * sets `revalidateVisible` covers. A GOTY potential is routinely neither: the
   * Steam auto-fill mints provisional rows that land at the end of the table and
   * that nobody has voted on. Such a row was never sent to `giFlush` by anyone,
   * so `assets[appid]` stayed empty forever, and for a MODERN appid that means
   * no art at all — `artCands` can otherwise only guess the legacy CDN shapes,
   * and those 404 for anything recent (see the note there).
   *
   * `useQueueArt` reaches `queueAssets` and deliberately not `revalidate`:
   * `queueAssets` batches at GI_MAX_BATCH and drains the remainder, where
   * `revalidate` passes whatever it is handed straight to one request.
   */
  useQueueArt(v.gRows.flatMap((r) => r.games.map((g) => g.id)));

  const drag = useDrag({
    zone: 'goty',
    dragging: !!s.drag,
    dragOver: s.dragOver && s.dragOver.zone === 'goty' ? s.dragOver : null,
    onStart: (row, id) => {
      const g = s.gById(id);
      s.dragStart({ zone: 'goty', row, id, name: g ? g.n.toUpperCase() : '' });
    },
    onOver: (ov) => s.setDragOver(ov),
    onDrop: () => s.dragEnd(),
    onCancel: () => s.dragCancel(),
  });

  const tapCap = (id: GameId): void => {
    if (drag.suppressed()) return;
    s.openSheet('goty', id);
  };

  /** `gSkip` (INVENTORY §4.4): the last unplaced game cannot be rotated away. */
  const skip = (): void => {
    if (v.gTodoN <= 1) {
      s.toast('LAST ONE — PLACE IT OR LEAVE IT');
      return;
    }
    void s.skipTodoId(v.gFeat.id);
  };

  return (
    <div
      data-screen-label="Game of the year"
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        // Full screen since DESIGN-CHANGELOG 2026-09-03 — see Interests.tsx.
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* the page's own bar: `◂ MY GAMES` · `GOTY 2026?` · the privacy line -- */}
      <PageBar
        title={
          <>
            GOTY {v.gotyYear}
            <span style={{ color: 'var(--acc2)' }}>?</span>
          </>
        }
        sub={'THE PARTY CAN SEE THIS ON YOUR PAGE · ' + v.gPotsTxt}
      />

      {/* the year row (AA phase 5) ------------------------------------------- */}
      <div data-hs="1" style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', padding: '12px 14px 0', fontFamily: MONO, fontSize: 10 }}>
        {v.yearRow.map((y) => (
          <span
            key={y.key}
            onClick={() => void s.setGotyYear(y.y)}
            style={{ border: '1px solid ' + y.bd, color: y.clr, background: y.bg, height: 40, display: 'flex', alignItems: 'center', padding: '0 14px', marginLeft: -1, position: 'relative', zIndex: y.on ? 1 : 0, cursor: 'pointer', flex: 'none', boxSizing: 'border-box' }}
          >
            {y.l}
          </span>
        ))}
        {v.moreYears.length ? (
          <span onClick={() => s.toggleGotyYearMenu()} style={{ marginLeft: 10, height: 40, display: 'flex', alignItems: 'center', color: 'var(--gt-text-5)', cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none' }}>
            + ANOTHER YEAR ▾
          </span>
        ) : null}
      </div>
      {v.yearMenuOpen ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, padding: '8px 14px 0', fontFamily: MONO, fontSize: 10 }}>
          {v.moreYears.map((y) => (
            <span key={y} onClick={() => void s.setGotyYear(y)} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', cursor: 'pointer', boxSizing: 'border-box' }}>
              {y}
            </span>
          ))}
        </div>
      ) : null}

      {/* add potentials + steam auto-fill --------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px 0' }}>
        <SearchBox
          value={v.gq}
          onChange={(e) => s.setGq(e.target.value)}
          placeholder="ADD A POTENTIAL…"
          prefix="+"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
        {v.hasGMatches ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {v.gMatches.map((m) => (
              <span
                key={m.key}
                onClick={() => void s.addPot(m.id)}
                style={{
                  border: '1px solid var(--acc)',
                  color: 'var(--acc)',
                  fontFamily: MONO,
                  fontSize: 10,
                  padding: '11px 12px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                + {m.name} <span style={{ color: 'var(--gt-faint)' }}>{m.yearTxt}</span>
              </span>
            ))}
          </div>
        ) : null}
        <div
          onClick={() => void s.steamFill()}
          style={{
            border: '1px solid ' + v.steamBd,
            color: v.steamClr,
            fontFamily: MONO,
            fontSize: 10,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            // `steamFill`'s own guard is what actually blocks a second run; this
            // is the cursor agreeing with it.
            cursor: v.steamBusy ? 'progress' : 'pointer',
            boxSizing: 'border-box',
          }}
        >
          {v.steamLbl}
        </div>
        <div style={{ fontFamily: MONO, fontSize: '8.5px', color: 'var(--gt-muted-2)', textAlign: 'center' }}>{v.steamSub}</div>
      </div>

      {/* THE BOX SITS ABOVE THE FILTER RAIL, full width and 44px tall — the rail
          scrolls horizontally, and a field inside a horizontal scroller is not
          reachable with a thumb. Box then filter, the desktop order read top to
          bottom rather than left to right (DESIGN-CHANGELOG 2026-08-28). */}
      <div style={{ padding: '12px 14px 0' }}>
        <SearchBox
          value={v.bq}
          onChange={(e) => s.setBq('goty', e.target.value)}
          onClear={() => s.setBq('goty', '')}
          placeholder="SEARCH POTENTIALS…"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
      </div>
      {/* filter bar ------------------------------------------------------- */}
      <div data-hs="1" style={{ display: 'flex', gap: 6, alignItems: 'center', overflowX: 'auto', padding: '12px 14px 0' }}>
        <div
          onClick={() => s.toggleFltOpen()}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid ' + v.fltBtnBd,
            color: v.fltBtnClr,
            background: v.fltBtnBg,
            fontFamily: MONO,
            fontSize: '10.5px',
            height: 40,
            padding: '0 13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          {v.fltBtnTxt}
        </div>
        {v.fltChips.map((c) => (
          <div
            key={c.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              padding: '9px 11px',
              whiteSpace: 'nowrap',
              fontFamily: MONO,
              fontSize: 10,
              flex: 'none',
            }}
          >
            {c.l}
            <span onClick={() => removeChip(s, c)} style={{ cursor: 'pointer', color: 'var(--gt-text-5)', padding: 2 }}>
              ✕
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px 26px' }}>
        {/* TO DO --------------------------------------------------------- */}
        {v.hasGFeat ? (
          <div
            data-gt-zone="goty"
            data-gt-row={v.gTodoRow}
            style={{
              display: 'flex',
              flexDirection: 'column',
              border: v.gTodoBd,
              background: v.gTodoBg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '11px 12px',
                borderBottom: '1px dashed var(--gt-border-3)',
              }}
            >
              <div style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text-5)', transform: 'translateY(0.13em)' }}>
                TO DO
              </div>
              <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>ONE AT A TIME</div>
              <div style={{ flex: 1 }} />
              <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--acc2)' }}>{v.gLeftTxt}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, padding: 12 }}>
              <div
                onClick={() => s.openGame(v.gFeat.id)}
                style={{
                  position: 'relative',
                  width: 104,
                  flex: 'none',
                  aspectRatio: '2/3',
                  border: '1px solid var(--gt-border-2)',
                  background: v.gFeat.fall,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                <CoverArt
                  style={{ position: 'absolute', inset: 0 }}
                  fs={13}
                  ini={v.gFeat.ini}
                  hasCover={featArt.hasCover}
                  cover={featArt.cover}
                />
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: v.gFeat.sheen,
                    mixBlendMode: 'screen',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div
                  onClick={() => s.openGame(v.gFeat.id)}
                  style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.25, color: 'var(--gt-text)', cursor: 'pointer' }}
                >
                  {v.gFeat.name}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>{v.gFeat.metaTxt}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                  {v.gBtns.map((b) => (
                    <div
                      key={b.key}
                      onClick={() => void s.placeTier(b.id, b.key)}
                      style={{
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: PIXEL,
                        fontSize: 11,
                        border: '1px solid ' + b.bd,
                        color: b.clr,
                        background: b.bg,
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span style={{ transform: 'translateY(0.13em)' }}>{b.t}</span>
                    </div>
                  ))}
                </div>
                <div
                  onClick={skip}
                  style={{
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    border: '1px solid var(--gt-border-2)',
                    color: 'var(--gt-text-5)',
                    fontFamily: MONO,
                    fontSize: 10,
                    letterSpacing: '.05em',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  SKIP FOR NOW ↻
                </div>
              </div>
            </div>
            <div style={{ padding: '0 12px 11px', fontFamily: MONO, fontSize: 8, color: 'var(--gt-tick)', lineHeight: 1.8 }}>
              {v.gLegendTxt}
              <br />
              TAP A PLACED GAME FOR OPTIONS · HOLD + DRAG TO REORDER, OR ONTO TO DO TO UNPLACE
            </div>
            {v.hasGUpNext ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  borderTop: '1px dashed var(--gt-border)',
                  padding: '10px 12px',
                }}
              >
                <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-faint)' }}>UP NEXT</div>
                {v.gUpNext.map((n) => (
                  <TodoThumb key={n.key} n={n} w={34} fs={7} />
                ))}
                {v.hasGMore ? (
                  <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-tick)' }}>{v.gMoreTxt}</div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* all done — still the unplace target, or the last game placed would
            have nothing to drag back TO (`hasGFeat` is false exactly here) --- */}
        {v.gAllDone ? (
          <div
            data-gt-zone="goty"
            data-gt-row={v.gTodoRow}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              border: v.gTodoBd,
              background: v.gTodoBg,
              padding: '26px 16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--acc)' }}>✓</div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-muted)', lineHeight: 1.9 }}>
              ALL PLACED — ADD MORE POTENTIALS ABOVE
            </div>
          </div>
        ) : null}

        {/* tier rows ------------------------------------------------------ */}
        {v.gRows.map((r) => (
          <div
            key={r.key}
            style={{ display: 'flex', flexDirection: 'column', border: '1px solid ' + r.bd, background: 'var(--gt-card)' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: r.labBg,
                borderBottom: '1px solid var(--gt-border)',
                padding: '9px 12px',
              }}
            >
              <div style={{ fontFamily: PIXEL, fontSize: 12, color: r.bigClr, transform: 'translateY(0.13em)' }}>{r.big}</div>
              <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.08em', color: 'var(--gt-text-5)' }}>
                {r.sub}
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>{r.cntTxt}</div>
            </div>
            <div
              data-hs="1"
              data-gt-zone="goty"
              data-gt-row={r.key}
              style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: 12 }}
            >
              {r.games.map((g) => (
                <TierTile key={g.key} g={g} onTap={() => tapCap(g.id)} onPointerDown={drag.capPD(r.key, g.id)} />
              ))}
              {r.isEmpty ? (
                <div
                  style={{
                    alignSelf: 'center',
                    fontFamily: MONO,
                    fontSize: '9.5px',
                    color: 'var(--gt-ghost)',
                    padding: '14px 2px',
                  }}
                >
                  {r.hint}
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
